import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

const useInterviewPreviewScrollNavigation = (totalAnswers: number) => {
  const [touchMoveDelta, setTouchMoveDelta] = useState(0);
  const [touchStartPoint, setTouchStartPoint] = useState(0);

  const [curAnswer, setCurAnswer] = useState(0);

  const playVideoAtIndex = useCallback((index: number) => {
    const answersTopsEls = document.querySelectorAll('.answer-top');
    const videoElems = document.querySelectorAll('video');
    if (!answersTopsEls)
      return

    answersTopsEls[index].scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    videoElems.forEach((video) => {
      video.pause();
    });
    videoElems[index].play().catch((err) => {
      if (err.name !== 'NotAllowedError') {
        Sentry.captureException(err);
      }
    });
    setCurAnswer(index);
  }, [])

  const handleNextStep = useCallback(() => {
    const nextAnswer = curAnswer + 1;
    if (totalAnswers === undefined) {
      return;
    }

    if (nextAnswer < totalAnswers) {
      playVideoAtIndex(nextAnswer);
    }
  }, [curAnswer, totalAnswers, playVideoAtIndex]);

  const handlePrevStep = useCallback(() => {
    const prevAnswer = curAnswer - 1;
    if (prevAnswer >= 0) {
      playVideoAtIndex(prevAnswer);
    }
  }, [curAnswer, playVideoAtIndex]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleTouchStart = (e) => {
      setTouchStartPoint(e.touches[0].clientY);
    };

    const handleTouchMove = (e) => {
      setTouchMoveDelta(e.touches[0].clientY - touchStartPoint);
    };

    const handleTouchEnd = () => {
      if (touchMoveDelta < 0) {
        handleNextStep();
      }

      if (touchMoveDelta > 0) {
        const prevAnswer = curAnswer - 1;
        if (prevAnswer >= 0) {
          playVideoAtIndex(prevAnswer);
        }
      }

      setTouchMoveDelta(0);
      setTouchStartPoint(0);
    };

    document.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [
    curAnswer,
    handleNextStep,
    touchMoveDelta,
    touchStartPoint,
    playVideoAtIndex,
  ]);

  return {
    handlePrevStep,
    handleNextStep,
  };
};

export default useInterviewPreviewScrollNavigation;
